import React from "react";
import {
  Users,
  FolderOpen,
  ChevronLeft,
  ChevronRight,
  Crown,
  Home,
  LogOut,
} from "lucide-react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

interface NavItem {
  id: string;
  label: string;
  icon: React.FC<{ className?: string }>;
  path: string;
}

const AdvisorSidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const clientsMatch = useMatch("/advisor/:advisorAccountId/clients");
  const groupsMatch = useMatch("/advisor/:advisorAccountId/groups");
  const groupDetailMatch = useMatch("/advisor/:advisorAccountId/groups/:groupId");
  const advisorAccountId =
    clientsMatch?.params.advisorAccountId ||
    groupsMatch?.params.advisorAccountId ||
    groupDetailMatch?.params.advisorAccountId;

  const navItems: NavItem[] = [
    {
      id: "clients",
      label: "Clients",
      icon: Users,
      path: advisorAccountId ? `/advisor/${advisorAccountId}/clients` : "/advisor/clients",
    },
    {
      id: "groups",
      label: "Groups",
      icon: FolderOpen,
      path: advisorAccountId ? `/advisor/${advisorAccountId}/groups` : "/advisor/groups",
    },
  ];

  const userRoles = user?.userRoles?.map((ur) => ur.role) || [];
  const isAdmin = userRoles.includes("ADMIN");
  const isInvestor = userRoles.includes("INVESTOR") || userRoles.length === 0;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isActive = (item: NavItem) => {
    if (item.id === "clients") return location.pathname.includes("/clients");
    if (item.id === "groups") return location.pathname.includes("/groups");
    return false;
  };

  return (
    <aside
      className={`bg-rp-black border-r border-rp-separator flex flex-col shrink-0 transition-all duration-300 ${
        isCollapsed ? "w-16" : "w-64"
      } hidden md:flex`}
    >
      {/* Header */}
      <div
        className={`flex items-center ${
          isCollapsed ? "flex-col justify-center px-2 gap-3" : "justify-between px-6"
        } pt-6 pb-4`}
      >
        {!isCollapsed && (
          <div className="flex items-center">
            <img src="/favicon.png" alt="Plutus" className="h-8 w-8 mr-2" />
            <img src="/dark.png" alt="Plutus" className="h-8 w-auto" />
            <span className="ml-2 text-sm font-medium text-rp-amber">for Advisors</span>
          </div>
        )}
        {isCollapsed && (
          <img src="/favicon.png" alt="Plutus" className="h-8 w-8" />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded-lg hover:bg-rp-separator text-rp-grey transition-colors"
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4" />
          ) : (
            <ChevronLeft className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Advisor identity */}
      {!isCollapsed && (
        <div className="px-6 pt-4 pb-6">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 rounded-lg flex items-center justify-center bg-rp-separator">
              <Users className="w-8 h-8 text-rp-amber" />
            </div>
            <p className="text-sm font-medium text-rp-grey mt-2 text-center">
              {user?.name || "Advisor"}
            </p>
          </div>
        </div>
      )}
      {isCollapsed && (
        <div className="px-2 pt-4 pb-4">
          <div className="w-12 h-12 mx-auto rounded-lg flex items-center justify-center bg-rp-separator">
            <Users className="w-6 h-6 text-rp-amber" />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 space-y-1 mt-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 rounded-lg transition-all text-sm font-medium ${
                isCollapsed ? "justify-center px-2 py-2.5" : "px-4 py-2.5"
              } ${
                active
                  ? "bg-rp-amber/10 text-rp-amber border-l-[3px] border-rp-amber"
                  : "text-rp-grey hover:text-rp-grey hover:bg-rp-separator border-l-[3px] border-transparent"
              }`}
            >
              <Icon className="w-5 h-5 shrink-0" />
              {!isCollapsed && <span className="flex-1 text-left">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Bottom Navigation */}
      <div className="mt-auto" />
      <div className="border-t border-rp-separator p-2 space-y-1">
        {isAdmin && (
          <button
            onClick={() => navigate("/admin/dashboard")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-red-700 text-white hover:bg-red-900/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Crown className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Admin Dashboard</span>}
          </button>
        )}

        {isInvestor && (
          <button
            onClick={() => navigate("/alpha")}
            className={`w-full flex items-center gap-3 text-xs font-medium rounded-lg transition-colors border border-rp-amber text-white hover:bg-rp-amber/20 ${
              isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
            }`}
          >
            <Home className="w-5 h-5 shrink-0" />
            {!isCollapsed && <span>Main Dashboard</span>}
          </button>
        )}

        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 text-sm font-medium rounded-lg transition-colors text-red-500 hover:bg-red-500/20 hover:text-red-500 ${
            isCollapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"
          }`}
        >
          <LogOut className="w-5 h-5 shrink-0" />
          {!isCollapsed && <span>Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AdvisorSidebar;
